import { Spacer, Typography } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { l } from "@shared/locales/i18n";

import * as styles from "./SecureYourAccount.styles";

interface Props {
  children: ReactNode;
  isSkippable?: boolean;
  isChangeFlow?: boolean;
  isModal?: boolean;
}

const SecureYourAccount = ({ children, isSkippable, isChangeFlow, isModal }: Props) => {
  const { t } = useTranslation();

  const renderContent = () => {
    if (isChangeFlow) {
      return (
        <>
          <Typography variant="h4" intention="neutral" colorIntensity="bold">
            {t(l.mfa.chooseAuthenticationMethod.changeTitle)}
          </Typography>
          <Typography>{t(l.mfa.chooseAuthenticationMethod.subtitle)}</Typography>
        </>
      );
    }

    if (isModal) {
      return (
        <>
          <Typography
            customCss={styles.title}
            variant="h4"
            intention="neutral"
            colorIntensity="bold"
          >
            {t(l.mfa.chooseAuthenticationMethod.title)}
          </Typography>
          <Typography>{t(l.mfa.chooseAuthenticationMethod.subtitle)}</Typography>
          <Typography isMarginless>{renderMandatoryWarning()}</Typography>
        </>
      );
    }

    return (
      <div css={styles.header}>
        <Typography
          customCss={styles.title}
          variant="h1"
          isMarginless
          intention="neutral"
          colorIntensity="bold"
        >
          {t(l.mfa.chooseAuthenticationMethod.title)}
        </Typography>
        <Typography isMarginless>{t(l.mfa.chooseAuthenticationMethod.subtitle)}</Typography>
        <Spacer size="large" />
        <Typography isMarginless>{renderMandatoryWarning()}</Typography>
      </div>
    );
  };

  const renderMandatoryWarning = () => {
    if (isSkippable) {
      return (
        <Trans i18nKey={l.mfa.chooseAuthenticationMethod.willBeMandatorySoon}>
          <strong>This will be mandatory soon.</strong> Set up today to add an extra layer of
          security.
        </Trans>
      );
    }

    return t(l.mfa.chooseAuthenticationMethod.thisIsMandatory);
  };

  return (
    <>
      {renderContent()}
      <Spacer size="xlarge" />
      {children}
    </>
  );
};

export { SecureYourAccount };
